export type BrushKind = 'pen' | 'marker' | 'spray' | 'eraser' | 'fill';
export interface BrushSettings { kind: BrushKind; color: string; width: number; opacity: number }
export interface BrushPoint { x: number; y: number }
interface SprayRun { offsetX: number; bend: number; length: number; radius: number }
export interface BrushDab extends BrushPoint {
  radius: number; alpha: number;
  /** Stroke seed varies immutable spray stamps consistently across rendering targets. */
  textureSeed?: number;
  splatter?: { angle: number; reach: number; width: number };
  drip?: SprayRun;
  /** Start/stop deposits carry coherent irregular haze, rather than a round cap. */
  burst?: { rotation: number; phase: number };
  runs?: SprayRun[];
  droplets?: { offsetX: number; offsetY: number; radius: number; stretch: number; angle: number; alpha: number }[];
}
export type RotationDirection = 'clockwise' | 'counterclockwise';
export interface DrawingPixels { data: Uint8ClampedArray; width: number; height: number }
export const DEFAULT_BRUSH: BrushSettings = { kind: 'pen', color: '#000000', width: 36, opacity: 1 };

/** Distance-spaced dabs keep coverage stable across fast and slow pointer event delivery. */
export class BrushStroke {
  private last: BrushPoint | null = null;
  private remainder = 0;
  private seed: number;
  private index = 0;
  private lastDrip = -24;
  private lastSplatter = -24;
  private textureSeed: number;
  private ended = false;
  private travel = 0;
  readonly settings: BrushSettings;
  constructor(settings: BrushSettings, seed = Date.now()) {
    this.settings = { ...settings, width: Math.max(1, Math.min(512, Number.isFinite(settings.width) ? settings.width : DEFAULT_BRUSH.width)), opacity: Math.max(0, Math.min(1, settings.opacity)) };
    this.seed = seed >>> 0;
    this.textureSeed = this.seed;
  }
  private random() {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }
  private dab(point: BrushPoint): BrushDab[] {
    const { kind, width, opacity } = this.settings;
    if (kind === 'fill') return []; // Bucket fill is a discrete tap, never a stroke.
    const dab: BrushDab = { ...point, radius: width / 2, alpha: kind === 'marker' ? opacity * .28 : opacity };
    if (kind === 'spray') {
      dab.textureSeed = this.textureSeed;
      const chance = this.random();
      if (width >= 12 && this.index - this.lastDrip >= 24 && chance < .028) {
        dab.drip = { offsetX: (this.random() - .5) * dab.radius * .6, bend: (this.random() - .5) * dab.radius * .16,
          length: dab.radius * (.3 + Math.pow(this.random(), 1.5) * 1.85), radius: Math.max(.65, dab.radius * (.042 + this.random() * .065)) };
        this.lastDrip = this.index;
      }
      if (width >= 12 && this.index - this.lastSplatter >= 24 && this.random() < .025) {
        dab.splatter = { angle: this.random() * Math.PI * 2, reach: .3 + this.random() * .5, width: .18 + this.random() * .15 };
        this.lastSplatter = this.index;
      }
      if (this.index === 0) this.deposit(dab);
      this.index++;
    }
    return [dab];
  }
  private deposit(dab: BrushDab) {
    dab.burst = { rotation: this.random() * Math.PI * 2, phase: this.random() * Math.PI * 2 };
    // Fine overspray surrounds a connected cloud; a handful of larger specks give varied texture.
    dab.droplets = Array.from({ length: 22 }, (_, index) => {
      const angle = this.random() * Math.PI * 2, reach = dab.radius * (1 + this.random() * .95);
      return { offsetX: Math.cos(angle) * reach, offsetY: Math.sin(angle) * reach,
        radius: Math.max(.35, dab.radius * (index < 3 ? .025 + this.random() * .055 : .009 + this.random() * .026)),
        stretch: .7 + this.random() * .9, angle, alpha: .15 + this.random() * .65 };
    });
    if (this.settings.width >= 12) {
      const count = this.random() < .65 ? 2 : 1;
      dab.runs = Array.from({ length: count }, (_, index) => ({ offsetX: dab.radius * ((index / count - .5) * .9 + this.random() * .25),
        bend: dab.radius * (this.random() - .5) * .15, length: dab.radius * (.42 + Math.pow(this.random(), 1.4) * 2.08),
        radius: Math.max(.65, dab.radius * (.042 + this.random() * .065)) }));
    }
  }
  start(point: BrushPoint): BrushDab[] { this.last = point; this.ended = false; this.travel = 0; this.remainder = 0; return this.dab(point); }
  move(point: BrushPoint): BrushDab[] {
    if (this.ended) return [];
    if (!this.last) return this.start(point);
    const previous = this.last;
    const distance = Math.hypot(point.x - previous.x, point.y - previous.y);
    this.travel += distance;
    const spacing = Math.max(.75, this.settings.width * .16);
    const result: BrushDab[] = [];
    let travel = spacing - this.remainder;
    for (; travel <= distance + 1e-8; travel += spacing) {
      const fraction = travel / distance;
      result.push(...this.dab({ x: previous.x + (point.x - previous.x) * fraction, y: previous.y + (point.y - previous.y) * fraction }));
    }
    this.remainder = (this.remainder + distance) % spacing;
    this.last = point;
    return result;
  }
  /** A time-based Spray pulse may deposit at the last point without geometric travel. */
  hold(): BrushDab[] { return this.ended || !this.last ? [] : this.dab(this.last); }
  /** Commit a terminal deposit only for a completed stroke, never for cancellation. */
  finish(point?: BrushPoint): BrushDab[] {
    if (this.ended || !this.last) return [];
    const result = point ? this.move(point) : [];
    if (this.settings.kind === 'spray' && this.travel > this.settings.width * .5) {
      const terminal: BrushDab = { ...this.last, radius: this.settings.width / 2, alpha: this.settings.opacity, textureSeed: this.textureSeed };
      this.deposit(terminal); result.push(terminal);
    }
    this.ended = true; return result;
  }
}

/** Spray concentration follows elapsed time; pointer delivery only queues its path.
 * Local wetness is a bounded pulse-time heuristic, so accent timing can vary with emission cadence. */
export class SprayFlow {
  private readonly engine: BrushStroke;
  private pending: BrushDab[] = [];
  private point: BrushPoint | null = null;
  private readonly textureSeed: number;
  private lastTime = 0;
  private wetness = 0;
  private travel = 0;
  private accentIndex = 0;
  private dripIndex = 0;
  private dripWetness = 0;
  private dripResidence = 0;
  private movingDripIndex = 0;
  private stopped = true;
  readonly settings: BrushSettings;
  constructor(settings: BrushSettings, seed = Date.now()) {
    if (settings.kind !== 'spray') throw new Error('SprayFlow requires Spray settings');
    this.engine = new BrushStroke(settings, seed); this.settings = this.engine.settings; this.textureSeed = seed >>> 0;
  }
  private dose(dabs: BrushDab[], elapsed: number, trackDrips = true) {
    if (!dabs.length || elapsed <= 0) return [];
    const boundedTime = Math.min(100, elapsed), amount = 1 - Math.exp(-240 * boundedTime / 1000 / dabs.length);
    // Geometric travel disperses local wet paint; pointer callback count adds none.
    const retention = Math.exp(-this.travel / Math.max(1, this.settings.width * .5));
    this.wetness = (this.wetness + boundedTime / 1000 * this.settings.opacity) * retention; this.travel = 0;
    // The immediate initial coat adds no virtual hold time to the drip budget.
    if (trackDrips) {
      if (retention < .5) { this.dripIndex = 0; this.dripWetness = 0; this.dripResidence = 0; }
      this.dripWetness = Math.min(.3, (this.dripWetness + boundedTime / 1000 * this.settings.opacity) * retention);
      this.dripResidence = Math.min(1.25, (this.dripResidence + boundedTime / 1000 * this.settings.opacity) * retention);
    }
    const result: BrushDab[] = dabs.map(({ drip: _drip, runs: _runs, splatter: _splatter, ...dab }) => ({ ...dab, alpha: dab.alpha * amount }));
    const last = result[result.length - 1];
    if (this.wetness >= .22) last.burst ||= { rotation: 0, phase: 0 };
    const threshold = .55 + noise(this.accentIndex, 0, this.textureSeed ^ 0x12f34ab) * .25;
    if (this.settings.width >= 12 && this.wetness >= threshold) {
      this.accentIndex++;
      last.burst ||= { rotation: 0, phase: 0 };
      this.wetness = 0;
    }
    const dripThreshold = this.dripIndex === 0 ? .20 + noise(0, 0, this.textureSeed ^ 0x12f34ab) * .015 : .16 + noise(this.dripIndex, 0, this.textureSeed ^ 0x12f34ab) * .08;
    if (this.settings.width >= 12 && this.dripWetness >= dripThreshold) {
      // Revisit a few seeded channels so held paint lengthens runs rather than multiplying them.
      const radius = last.radius, index = this.dripIndex++ % 4, seed = this.textureSeed ^ 0x48b12f;
      const growth = Math.min(1, this.dripResidence / 1.2);
      last.drip = { offsetX: (noise(index, 1, seed) - .5) * radius * .55, bend: (noise(index, 2, seed) - .5) * radius * .12,
        length: radius * (.45 + growth * (2 + noise(index, 3, seed) * 1.5)),
        radius: Math.max(.55, radius * (.035 + growth * .025 + noise(index, 4, seed) * .025)) };
      this.dripWetness = 0; // At most one inexpensive vector run per existing timed pulse.
    } else if (trackDrips && this.settings.width >= 12 && this.settings.opacity > 0 && retention < .95 && retention > .1) {
      const index = this.movingDripIndex++, seed = this.textureSeed ^ 0x719ac3;
      if (noise(index, 0, seed) < .012) {
        const radius = last.radius;
        last.drip = { offsetX: (noise(index, 1, seed) - .5) * radius * .55,
          bend: (noise(index, 2, seed) - .5) * radius * .08,
          length: Math.min(5, radius * (.12 + noise(index, 3, seed) * .14)),
          radius: Math.max(.4, radius * .02) };
      }
    }
    return result;
  }
  start(point: BrushPoint, now: number): BrushDab[] {
    this.pending = []; this.point = { ...point }; this.wetness = 0; this.travel = 0; this.accentIndex = 0; this.dripIndex = 0; this.dripWetness = 0; this.dripResidence = 0; this.movingDripIndex = 0; this.stopped = false; this.lastTime = Number.isFinite(now) ? now : 0;
    return this.dose(this.engine.start(point), 1000 / 30, false);
  }
  move(point: BrushPoint): void {
    if (this.stopped) return;
    if (this.point) this.travel += Math.hypot(point.x - this.point.x, point.y - this.point.y);
    this.point = { ...point };
    for (const dab of this.engine.move(point)) this.pending.push(dab);
  }
  advance(now: number): BrushDab[] {
    if (this.stopped || !Number.isFinite(now) || now - this.lastTime < 1000 / 30 - 1e-6) return [];
    const elapsed = now - this.lastTime; this.lastTime = now;
    const dabs = this.pending.length ? this.pending : this.engine.hold(); this.pending = [];
    return this.dose(dabs, elapsed);
  }
  finish(point: BrushPoint, now: number): BrushDab[] {
    if (this.stopped) return [];
    this.move(point);
    for (const dab of this.engine.finish()) this.pending.push(dab);
    const elapsed = Number.isFinite(now) ? Math.max(0, now - this.lastTime) : 0;
    this.stopped = true;
    const dabs = this.pending.length ? this.pending : this.point ? [{ ...this.point, radius: this.settings.width / 2, alpha: this.settings.opacity, textureSeed: this.textureSeed }] : [];
    this.pending = [];
    return this.dose(dabs, elapsed);
  }
  cancel(): void { this.stopped = true; this.pending = []; }
}

const SPRAY_SPRITE_SIZE = 128;
const SPRAY_SPRITE_RADIUS = 40;
interface SprayAtlas {
  rgb: number[];
  sprites: HTMLCanvasElement[];
  warmers: Set<() => boolean>;
  cancelWarm?: () => void;
}
const sprayAtlases = new WeakMap<Document, Map<string, SprayAtlas>>();

function noise(x: number, y: number, seed: number) {
  let value = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ seed;
  value = Math.imul(value ^ value >>> 13, 1274126177);
  return ((value ^ value >>> 16) >>> 0) / 4294967296;
}

function colorAtlas(document: Document, color: string) {
  const hex = color.replace('#', ''), normalized = hex.length === 3 ? hex.split('').map(char => char + char).join('') : hex;
  const rgb = [0, 2, 4].map(index => parseInt(normalized.slice(index, index + 2), 16) || 0), key = rgb.join(',');
  let colors = sprayAtlases.get(document);
  if (!colors) { colors = new Map(); sprayAtlases.set(document, colors); }
  const cached = colors.get(key);
  if (cached) { colors.delete(key); colors.set(key, cached); return cached; }
  if (colors.size >= 4) {
    const oldest = colors.keys().next().value!, evicted = colors.get(oldest)!;
    evicted.cancelWarm?.(); evicted.warmers.clear(); colors.delete(oldest);
  }
  const atlas: SprayAtlas = { rgb, sprites: [], warmers: new Set() };
  colors.set(key, atlas); return atlas;
}

/** Upload once; no source canvas is repainted, resized or cleared after construction. */
function createSpraySprite(document: Document, rgb: number[], variant: number) {
  const canvas = document.createElement('canvas'); canvas.width = canvas.height = SPRAY_SPRITE_SIZE;
  const context = canvas.getContext('2d'); if (!context) return null;
  const image = context.createImageData(SPRAY_SPRITE_SIZE, SPRAY_SPRITE_SIZE), seed = (variant + 1) * 0x45d9f3b, deposit = variant >= 4;
  const phase = noise(variant, 0, seed) * Math.PI * 2, clusters = new Float64Array(17 * 17);
  // Cluster corners repeat across pixels; compute their hashes once per sprite.
  for (let y = 0; y < 17; y++) for (let x = 0; x < 17; x++) clusters[y * 17 + x] = noise(x, y, seed);
  for (let y = 0; y < SPRAY_SPRITE_SIZE; y++) for (let x = 0; x < SPRAY_SPRITE_SIZE; x++) {
    const dx = x + .5 - SPRAY_SPRITE_SIZE / 2, dy = y + .5 - SPRAY_SPRITE_SIZE / 2;
    const distance = Math.sqrt(dx * dx + dy * dy) / SPRAY_SPRITE_RADIUS;
    if (distance > 1.48) continue; // Transparent gutter keeps filtered sprite boundaries invisible.
    const index = (y * SPRAY_SPRITE_SIZE + x) * 4;
    let alpha = 1, shade = 0;
    if (distance > .45) {
      const gx = x / 8, gy = y / 8, ix = Math.floor(gx), iy = Math.floor(gy);
      const tx = gx - ix, ty = gy - iy, sx = tx * tx * (3 - 2 * tx), sy = ty * ty * (3 - 2 * ty);
      const a = clusters[iy * 17 + ix], b = clusters[iy * 17 + ix + 1], c = clusters[(iy + 1) * 17 + ix], d = clusters[(iy + 1) * 17 + ix + 1];
      const cluster = (a + (b - a) * sx) * (1 - sy) + (c + (d - c) * sx) * sy;
      const grain = noise(x, y, seed ^ 0x6a09e667);
      let extent = 1.14 + cluster * .14;
      if (deposit) { const angle = Math.atan2(dy, dx); extent = 1.22 + cluster * .1 + Math.sin(angle * 3 + phase) * .07 + Math.sin(angle * 7 - phase) * .05; }
      const fade = Math.max(0, Math.min(1, (extent - distance) / (extent - .45)));
      alpha = fade * fade * (3 - 2 * fade) * (.25 + grain * .75);
      if (distance > .9 && grain > .965) alpha = Math.max(alpha, (1.48 - distance) * (.18 + cluster * .3));
      const fringe = Math.min(1, (distance - .45) / .55);
      shade = (grain - .5) * 24 * fringe * fringe;
    }
    if (alpha <= 0) continue;
    image.data[index] = rgb[0] + shade; image.data[index + 1] = rgb[1] + shade * .8; image.data[index + 2] = rgb[2] + shade * .7;
    image.data[index + 3] = Math.round(alpha * 255);
  }
  context.putImageData(image, 0, 0); return canvas;
}

function warmSpray(document: Document, atlas: SprayAtlas) {
  if (atlas.cancelWarm || atlas.sprites.length === 6 || !atlas.warmers.size) return;
  const view = document.defaultView; if (!view) return;
  const idle = typeof view.requestIdleCallback === 'function' && typeof view.cancelIdleCallback === 'function';
  let cancelled = false;
  const callback = (deadline?: IdleDeadline) => {
    if (cancelled) return;
    atlas.cancelWarm = undefined;
    if (!atlas.warmers.size) return;
    if (document.visibilityState === 'hidden' || [...atlas.warmers].some(guard => !guard())) { atlas.warmers.clear(); return; }
    if (deadline && !deadline.didTimeout && deadline.timeRemaining() < 4) { warmSpray(document, atlas); return; }
    const sprite = createSpraySprite(document, atlas.rgb, atlas.sprites.length);
    if (!sprite) { atlas.warmers.clear(); return; }
    atlas.sprites.push(sprite);
    if (atlas.sprites.length === 6) atlas.warmers.clear(); else warmSpray(document, atlas);
  };
  const id = idle ? view.requestIdleCallback(callback) : view.setTimeout(() => callback(), 32);
  atlas.cancelWarm = () => { cancelled = true; if (idle) view.cancelIdleCallback(id); else view.clearTimeout(id); atlas.cancelWarm = undefined; };
}

/** Idle preparation yields between variants; cancellation never discards uploaded sources. */
export function prepareSpray(document: Document, color: string, shouldWarm: () => boolean = () => true): () => void {
  const atlas = colorAtlas(document, color); if (atlas.sprites.length === 6) return () => {};
  const guard = () => shouldWarm(); atlas.warmers.add(guard); warmSpray(document, atlas);
  let cancelled = false;
  return () => { if (cancelled) return; cancelled = true; atlas.warmers.delete(guard); if (!atlas.warmers.size) atlas.cancelWarm?.(); };
}

function sprayAtlas(document: Document, color: string) {
  const atlas = colorAtlas(document, color); atlas.cancelWarm?.();
  while (atlas.sprites.length < 6) {
    const sprite = createSpraySprite(document, atlas.rgb, atlas.sprites.length); if (!sprite) break;
    atlas.sprites.push(sprite);
  }
  if (atlas.sprites.length === 6) atlas.warmers.clear();
  return atlas.sprites;
}

export function paintDabs(context: CanvasRenderingContext2D, dabs: BrushDab[], settings: BrushSettings) {
  if (settings.kind === 'fill') return;
  const atlas = settings.kind === 'spray' && dabs.length ? sprayAtlas(context.canvas.ownerDocument, settings.color) : null;
  context.save();
  context.globalCompositeOperation = settings.kind === 'eraser' ? 'destination-out' : 'source-over';
  context.fillStyle = settings.color;
  for (const dab of dabs) {
    context.globalAlpha = dab.alpha;
    if (settings.kind === 'spray') {
      // Runs start inside a heavier deposit and taper towards differently sized drops.
      for (const drip of [...(dab.runs || []), ...(dab.drip ? [dab.drip] : [])]) {
        const x = dab.x + drip.offsetX, endX = x + drip.bend, endY = dab.y + dab.radius + drip.length;
        context.globalAlpha = dab.alpha * .7; context.strokeStyle = settings.color; context.fillStyle = settings.color;
        context.beginPath(); context.moveTo(x - drip.radius * 1.5, dab.y + dab.radius * .4);
        context.quadraticCurveTo(x - drip.radius * .55, dab.y + dab.radius, endX - drip.radius, endY - drip.radius);
        context.quadraticCurveTo(endX - drip.radius * 1.5, endY + drip.radius, endX, endY + drip.radius * 1.2);
        context.quadraticCurveTo(endX + drip.radius * 1.5, endY + drip.radius, endX + drip.radius, endY - drip.radius);
        context.quadraticCurveTo(x + drip.radius * .55, dab.y + dab.radius, x + drip.radius * 1.5, dab.y + dab.radius * .4);
        context.closePath(); context.fill();
        context.globalAlpha = dab.alpha;
      }
      if (dab.droplets) for (const drop of dab.droplets) {
        context.globalAlpha = dab.alpha * drop.alpha; context.beginPath();
        context.ellipse(dab.x + drop.offsetX, dab.y + drop.offsetY, drop.radius, drop.radius * drop.stretch, drop.angle, 0, Math.PI * 2); context.fill();
      }
      context.globalAlpha = dab.alpha;
      // Occasional directional accents retain splatter without another mutable raster.
      if (dab.splatter) {
        const { angle, reach, width } = dab.splatter, centre = dab.radius * (.85 + reach * .5);
        context.globalAlpha = dab.alpha * .35; context.beginPath();
        context.ellipse(dab.x + Math.cos(angle) * centre, dab.y + Math.sin(angle) * centre,
          dab.radius * (.4 + reach * .5), dab.radius * width, angle, 0, Math.PI * 2); context.fill();
        context.globalAlpha = dab.alpha;
      }
      const variation = noise(Math.round(dab.x * 16), Math.round(dab.y * 16), dab.textureSeed || 0);
      const sprite = atlas?.[dab.burst ? 4 + Math.floor(variation * 2) : Math.floor(variation * 4)];
      if (sprite) {
        const size = SPRAY_SPRITE_SIZE * dab.radius / SPRAY_SPRITE_RADIUS;
        context.drawImage(sprite, dab.x - size / 2, dab.y - size / 2, size, size);
      }
      continue;
    }
    context.beginPath(); context.arc(dab.x, dab.y, dab.radius, 0, Math.PI * 2); context.fill();
  }
  context.restore();
}

/** Four-connected fill. Small premultiplied-RGBA tolerance includes antialiased fringes,
 * but does not cross a contrasting border. Transparent RGB is deliberately irrelevant.
 * Mutates only the selected region and returns the number of changed pixels. */
export function fillRegion(data: Uint8ClampedArray, width: number, height: number, point: BrushPoint, color: string, opacity: number, tolerance = 20) {
  return fillMatchingRegion(data, data, width, height, point, color, opacity, tolerance);
}

/** Detect boundaries in a frozen saved-art + draft composite, but paint only the draft.
 * Reference and destination must not overlap: saved pixels are never copied or changed. */
export function fillRegionAgainstReference(draft: Uint8ClampedArray, reference: Uint8ClampedArray, width: number, height: number, point: BrushPoint, color: string, opacity: number, tolerance = 20, eligible?: Uint8Array): number {
  if (draft.buffer === reference.buffer && draft.byteOffset < reference.byteOffset + reference.byteLength && reference.byteOffset < draft.byteOffset + draft.byteLength) return 0;
  return fillMatchingRegion(draft, reference, width, height, point, color, opacity, tolerance, eligible);
}

function fillMatchingRegion(draft: Uint8ClampedArray, reference: Uint8ClampedArray, width: number, height: number, point: BrushPoint, color: string, opacity: number, tolerance: number, eligible?: Uint8Array): number {
  const count = width * height;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || count > 4_194_304 || draft.length !== count * 4 || reference.length !== count * 4) return 0;
  if (!Number.isFinite(point.x) || !Number.isFinite(point.y) || !Number.isFinite(opacity)) return 0;
  const x = Math.floor(point.x), y = Math.floor(point.y);
  if (x < 0 || x >= width || y < 0 || y >= height) return 0;
  const paintAlpha = Math.max(0, Math.min(1, opacity));
  if (paintAlpha === 0) return 0;
  const hex = color.replace('#', '');
  const normalized = /^[\da-f]{3}$/i.test(hex) ? hex.split('').map(char => char + char).join('') : hex;
  if (!/^[\da-f]{6}$/i.test(normalized)) return 0;
  const paintR = parseInt(normalized.slice(0, 2), 16), paintG = parseInt(normalized.slice(2, 4), 16), paintB = parseInt(normalized.slice(4, 6), 16);
  const start = y * width + x, offset = start * 4;
  const targetAlpha = reference[offset + 3];
  const targetR = reference[offset] * targetAlpha / 255, targetG = reference[offset + 1] * targetAlpha / 255, targetB = reference[offset + 2] * targetAlpha / 255;
  const allowedDifference = Math.max(0, Math.min(64, Number.isFinite(tolerance) ? tolerance : 20));
  const visited = new Uint8Array(count), queue = new Uint32Array(count);
  let head = 0, tail = 1, changed = 0;
  queue[0] = start; visited[start] = 1;
  const enqueue = (pixel: number) => { if (!visited[pixel]) { visited[pixel] = 1; queue[tail++] = pixel; } };
  while (head < tail) {
    const pixel = queue[head++], index = pixel * 4;
    if (eligible && (eligible.length !== count || !eligible[pixel])) continue;
    const alpha = reference[index + 3];
    if (Math.abs(alpha - targetAlpha) > allowedDifference || Math.abs(reference[index] * alpha / 255 - targetR) > allowedDifference || Math.abs(reference[index + 1] * alpha / 255 - targetG) > allowedDifference || Math.abs(reference[index + 2] * alpha / 255 - targetB) > allowedDifference) continue;
    const oldAlpha = draft[index + 3] / 255, retainedAlpha = oldAlpha * (1 - paintAlpha), newAlpha = paintAlpha + retainedAlpha;
    // Opaque paint and blank draft pixels are common and need no color division.
    const resultR = retainedAlpha === 0 ? paintR : Math.round((paintR * paintAlpha + draft[index] * retainedAlpha) / newAlpha);
    const resultG = retainedAlpha === 0 ? paintG : Math.round((paintG * paintAlpha + draft[index + 1] * retainedAlpha) / newAlpha);
    const resultB = retainedAlpha === 0 ? paintB : Math.round((paintB * paintAlpha + draft[index + 2] * retainedAlpha) / newAlpha);
    const resultAlpha = Math.round(newAlpha * 255);
    if (resultR !== draft[index] || resultG !== draft[index + 1] || resultB !== draft[index + 2] || resultAlpha !== draft[index + 3]) {
      draft[index] = resultR; draft[index + 1] = resultG; draft[index + 2] = resultB; draft[index + 3] = resultAlpha; changed++;
    }
    if (pixel % width > 0) enqueue(pixel - 1);
    if (pixel % width < width - 1) enqueue(pixel + 1);
    if (pixel >= width) enqueue(pixel - width);
    if (pixel < count - width) enqueue(pixel + width);
  }
  return changed;
}

/** Exact quarter-turn remapping preserves edge pixels and alpha without resampling. */
export function rotateDrawingPixels(image: DrawingPixels, direction: RotationDirection) {
  const { width, height, data } = image;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 || width * height > 4_194_304 || data.length !== width * height * 4) throw new Error('Invalid drawing dimensions');
  const rotated = new Uint8ClampedArray(data.length);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    const nextX = direction === 'clockwise' ? height - 1 - y : y;
    const nextY = direction === 'clockwise' ? x : width - 1 - x;
    const source = (y * width + x) * 4, destination = (nextY * height + nextX) * 4;
    rotated[destination] = data[source]; rotated[destination + 1] = data[source + 1];
    rotated[destination + 2] = data[source + 2]; rotated[destination + 3] = data[source + 3];
  }
  return { data: rotated, width: height, height: width };
}

/** The caller owns snapshots; image data comes from getImageData, never a live pixel buffer. */
export class DrawingHistory<T> {
  private snapshots: T[];
  private cursor = 0;
  constructor(initial: T, readonly limit = 16) { this.snapshots = [initial]; }
  get canUndo() { return this.cursor > 0; }
  get canRedo() { return this.cursor < this.snapshots.length - 1; }
  get current() { return this.snapshots[this.cursor]; }
  /** Update an existing editable action without adding an undo step or discarding redo. */
  replaceCurrent(snapshot: T) { this.snapshots[this.cursor] = snapshot; }
  commit(snapshot: T) {
    this.snapshots = this.snapshots.slice(0, this.cursor + 1);
    this.snapshots.push(snapshot);
    if (this.snapshots.length > this.limit + 1) this.snapshots.shift();
    this.cursor = this.snapshots.length - 1;
  }
  undo(): T | undefined { if (!this.canUndo) return; return this.snapshots[--this.cursor]; }
  redo(): T | undefined { if (!this.canRedo) return; return this.snapshots[++this.cursor]; }
}

/** Exact RGBA equality includes invisible RGB and preserves no-op history decisions. */
export function sameDrawingPixels(first: Uint8ClampedArray, second: Uint8ClampedArray) {
  if (first.length !== second.length) return false;
  for (let index = 0; index < first.length; index++) if (first[index] !== second[index]) return false;
  return true;
}

export function hasPaint(data: Uint8ClampedArray) {
  // This runs during drawing notifications; RGB is irrelevant to visible paint.
  for (let alpha = 3; alpha < data.length; alpha += 4) if (data[alpha] > 0) return true;
  return false;
}
